import { inject, Injectable } from '@angular/core';
import {
    BreakpointObserver,
    BreakpointState,
    Breakpoints,
} from '@angular/cdk/layout';
import { AccessConstants } from '../../constants/access-constants';
import { ExternalLinks } from '../../constants/external-links';
import { LinkText } from '../../constants/link-text';
import { RouteConstants } from '../../constants/routes';
import { UserRole } from '../../enums/users/role';
import { SideNavigationIconLink } from '../../models/navigation-icon-link';
import { PublicAppConfiguration } from '../public-app-configuration/models/public-app-configuration';
import { AppStorageService } from '../app-storage/app-storage.service';

@Injectable({
    providedIn: 'root',
})
export class SidenavService {
    private readonly observer = inject(BreakpointObserver);
    private readonly localStorageService = inject(AppStorageService);

    constructor() {
        const initialValue = this.localStorageService.getSideNavCollapsed();

        if (initialValue === null) {
            this.observer
                .observe([
                    Breakpoints.Handset,
                    Breakpoints.XSmall,
                    Breakpoints.Small,
                ])
                .subscribe((screenSize: BreakpointState) => {
                    this.setSidenavCollapsedState(screenSize.matches);
                });
        } else {
            this.setSidenavCollapsedState(initialValue);
        }
    }

    setSidenavCollapsedState(isCollapsed: boolean): void {
        this.localStorageService.setSidenavCollapsed(isCollapsed);
    }

    isSideNavCollapsed(): boolean {
        return this.localStorageService.getSideNavCollapsed();
    }

    /**
     * Returns the side navigation icon links for a specific user role.
     * If the role is undefined, it returns an empty array.
     * @param role The user role to filter the links.
     * @param applicationConfiguration The application configuration to get the links.
     * @returns An array of side navigation icon links that match the user's role.
     */
    getSideNavigationIconLinksForRole(
        role: UserRole | undefined,
        applicationConfiguration: PublicAppConfiguration,
    ): SideNavigationIconLink[] {
        if (!role) return [];

        return this.getSideNavigationIconLinks(applicationConfiguration).filter(
            (link) => link.roles?.includes(role.valueOf()),
        );
    }

    /**
     * Returns the available side navigation icon links.
     */
    private getSideNavigationIconLinks(
        applicationConfiguration: PublicAppConfiguration,
    ): SideNavigationIconLink[] {
        if (!applicationConfiguration) return [];

        return [
            {
                roles: AccessConstants.getAllUserRoles(),
                link: {
                    url: RouteConstants.Dashboard.getFullRoute('INDEX'),
                    text: 'Dashboard',
                },
                icon: {
                    uri: 'assets/svg/dashboard.svg',
                    alt: 'Dashboard',
                },
            },
            {
                roles: AccessConstants.RolesThatCanCreateApplicationsForOtherCustomers,
                link: {
                    url: RouteConstants.Application.getFullRoute(
                        'contactLookup',
                    ),
                    text: LinkText.fccLicenseApplication,
                },
                icon: {
                    uri: 'assets/svg/new-document.svg',
                    alt: 'New document',
                },
            },
            {
                roles: [UserRole.Customer],
                link: {
                    url: RouteConstants.Application.getFullRoute(
                        'purposeOfFiling',
                    ),
                    text: LinkText.fccLicenseApplication,
                },
                icon: {
                    uri: 'assets/svg/new-document.svg',
                    alt: 'New document',
                },
            },
            {
                roles: [UserRole.Admin, UserRole.Customer, UserRole.Advisor],
                link: {
                    url: RouteConstants.Template.getFullRoute('INDEX'),
                    text: 'Templates',
                },
                icon: { uri: 'assets/svg/documents.svg', alt: 'Documents' },
            },
            {
                roles: [
                    UserRole.Admin,
                    UserRole.Advisor,
                    UserRole.CrmManager,
                    UserRole.Accounting,
                ],
                link: {
                    url: RouteConstants.ServiceFulfillment.getFullRoute(
                        'INDEX',
                    ),
                    text: 'Service Fulfillment',
                },
                icon: {
                    uri: 'assets/svg/lightning-bolt.svg',
                    alt: 'Lightning bolt',
                },
            },
            {
                roles: AccessConstants.RolesThatCanAccessAllOrders,
                link: {
                    url: RouteConstants.Order.getFullRoute('INDEX'),
                    text: 'Order Fulfillment',
                },
                icon: {
                    uri: 'assets/svg/document.svg',
                    alt: 'Document',
                },
            },
            {
                roles: AccessConstants.RolesThatCanCreateApplicationsAndTemplates,
                link: {
                    url: RouteConstants.Go.getFullRoute('purposeOfFiling'),
                    text: 'Cevo Go',
                },
                icon: {
                    uri: 'assets/svg/cevo-go-icon.svg',
                    alt: 'Cevo Go Icon',
                },
            },
            {
                roles: AccessConstants.RolesThatCanCreateApplicationsAndTemplates,
                link: {
                    url: RouteConstants.FrnFiling.getFullRoute('newFiling'),
                    text: 'FRN Filing',
                },
                icon: {
                    uri: 'assets/svg/frn-folder.svg',
                    alt: 'Folder',
                },
            },
            {
                roles: [UserRole.Customer],
                link: {
                    url: RouteConstants.ServiceFulfillment.getFullRoute(
                        'INDEX',
                    ),
                    text: 'Services & Filings',
                },
                icon: {
                    uri: 'assets/svg/lightning-bolt.svg',
                    alt: 'Lightning bolt',
                },
            },
            {
                roles: [UserRole.Customer],
                link: {
                    url: RouteConstants.Order.getFullRoute('INDEX'),
                    text: 'Orders',
                },
                icon: {
                    uri: 'assets/svg/document.svg',
                    alt: 'Document',
                },
            },
            {
                roles: [UserRole.Customer],
                link: {
                    url: RouteConstants.Payment.getFullRoute('INDEX'),
                    text: 'Payments',
                },
                icon: {
                    uri: 'assets/svg/credit-card.svg',
                    alt: 'Credit card',
                },
            },
            {
                roles: AccessConstants.RolesThatCanManageAllUsers,
                link: {
                    url: RouteConstants.User.getFullRoute('INDEX'),
                    text: 'Manage Users',
                },
                icon: { uri: 'assets/svg/users.svg', alt: 'Users' },
            },
            {
                roles: [
                    UserRole.Admin,
                    UserRole.Advisor,
                    UserRole.CrmManager,
                    UserRole.Accounting,
                ],
                link: {
                    external: true,
                    url: applicationConfiguration.civiCrmUrl ?? '',
                    text: 'CiviCRM',
                },
                icon: {
                    uri: 'assets/svg/civicrm.svg',
                    alt: 'CiviCRM',
                },
            },
            {
                roles: AccessConstants.EwaStaffRoles,
                link: {
                    url: RouteConstants.Reports.getFullRoute('INDEX'),
                    text: 'Reports',
                },
                icon: { uri: 'assets/svg/report.svg', alt: 'Report' },
            },
            {
                roles: AccessConstants.getAllUserRoles(),
                link: {
                    external: true,
                    url: ExternalLinks.enterpriseWireless.ewaConnect,
                    text: 'EWA Connect',
                },
                icon: {
                    uri: 'assets/svg/ewa-connect.svg',
                    alt: 'EWA Connect Logo',
                },
                onlyShowWhenCustomerIsMember: true,
            },
            {
                roles: AccessConstants.getAllUserRoles(),
                link: {
                    url: RouteConstants.Profile.getFullRoute('INDEX'),
                    text: 'My Profile',
                },
                icon: {
                    uri: 'assets/svg/profile.svg',
                    alt: 'Profile icon',
                },
                onlyShowWhenCollapsed: true,
            },
        ];
    }
}
